/* Pricing guide (pricing-cards section).
   Question groups (state / license stage / role) drive "deck" visibility.
   Decks are divs tagged data-state/data-stage/data-role; the controller
   derives which questions apply from the decks themselves:
   - stage question shows only when the selected state has >1 stage;
   - role question shows only when the selected state+stage has >1 role;
   - invalid selections fall back to the first available option.

   Two modes:
   - default: all applicable questions expanded, matching deck always shown.
   - guided ([data-ppc-guided]): one question at a time. Answered questions
     collapse to a "✓ Florida · Change" chip. Once every applicable question
     is answered, a "See your <state> courses" CTA routes to that path's
     course page (window.PCRoutes); when no route exists (or in the theme
     editor) the deck (courses) renders instead and scrolls into view.
     Deep-link params (?state= ?stage= ?role=) count as answers.

   State changes broadcast on the same pc:tabs-sync bus pc-tabs.js uses, so
   FAQ state toggles stay in step on pages that have them. */
(function () {
  'use strict';

  var SYNC_EVENT = 'pc:tabs-sync';
  var STAGE_ORDER = ['pre', 'post', 'ce', 'react', 'exam'];
  var GROUP_ORDER = ['state', 'stage', 'role'];

  function uniq(arr) {
    return arr.filter(function (v, i) { return arr.indexOf(v) === i; });
  }

  function init(root) {
    if (root.dataset.ppcInit === '1') return;
    root.dataset.ppcInit = '1';

    var decks = Array.prototype.slice.call(root.querySelectorAll('[data-ppc-deck]'));
    if (!decks.length) return;
    var groups = {};
    GROUP_ORDER.forEach(function (name) {
      groups[name] = root.querySelector('[data-ppc-group="' + name + '"]');
    });
    var guideEl = root.querySelector('.pc-pricing__guide');

    // guided-mobile: a non-wizard section (e.g. /pages/pricing) that behaves as a
    // one-question-at-a-time wizard on phones/tablets only; data-ppc-stay keeps
    // completion in place (reveal the deck) instead of routing to a funnel page.
    var guided = root.hasAttribute('data-ppc-guided') ||
      (root.hasAttribute('data-ppc-guided-mobile') && window.matchMedia('(max-width: 900px)').matches);
    var stayHere = root.hasAttribute('data-ppc-stay');
    var autoRoute = root.hasAttribute('data-ppc-autoroute');
    var navigating = false;
    var singleState = root.getAttribute('data-pc-single-state');
    var sel = { state: singleState || 'CA', stage: 'pre', role: 'agent' };
    var answered = { state: !!singleState, stage: false, role: false };

    try {
      var q = new URLSearchParams(window.location.search);
      var qs = (q.get('state') || '').toUpperCase();
      var qg = (q.get('stage') || '').toLowerCase();
      var qr = (q.get('role') || '').toLowerCase();
      if (qs && !singleState) { sel.state = qs; answered.state = true; }
      if (qg) { sel.stage = qg; answered.stage = true; }
      if (qr) { sel.role = qr; answered.role = true; }
    } catch (e) { /* no-op */ }

    if (!guided) { answered = { state: true, stage: true, role: true }; }

    function stagesFor(state) {
      var found = uniq(decks
        .filter(function (d) { return d.dataset.state === state; })
        .map(function (d) { return d.dataset.stage; }));
      return STAGE_ORDER.filter(function (s) { return found.indexOf(s) !== -1; });
    }

    function rolesFor(state, stage) {
      var raw = uniq(decks
        .filter(function (d) { return d.dataset.state === state && d.dataset.stage === stage; })
        .map(function (d) { return d.dataset.role; }));
      if (!guided) return raw;
      // Guided finder: a shared deck (role "both") serves agents and brokers
      // alike, but their destination pages differ — still ask the question.
      var out = [];
      raw.forEach(function (r) {
        if (r === 'both') { out.push('agent', 'broker'); }
        else { out.push(r); }
      });
      return uniq(out);
    }

    function chipLabelFor(group, value) {
      var btn = group.querySelector('[data-ppc-value="' + value + '"]');
      var title = btn && btn.querySelector('.pc-choice__title');
      return title ? title.textContent.trim() : value;
    }

    var routeCta = null;
    function renderRouteCta(dest) {
      if (!dest) {
        if (routeCta) routeCta.style.display = 'none';
        return;
      }
      if (!routeCta) {
        routeCta = document.createElement('a');
        routeCta.className = 'pc-btn pc-btn--primary pc-btn--lg pc-pricing__route-cta';
        var guide = root.querySelector('.pc-pricing__guide') || root;
        guide.appendChild(routeCta);
      }
      var stateName = sel.state === 'CA' ? 'California' : sel.state === 'FL' ? 'Florida' : 'your';
      routeCta.textContent = 'See your ' + stateName + ' courses';
      routeCta.setAttribute('href', dest);
      routeCta.style.display = '';
    }

    function renderGroup(name, applicable, mode, value, allowed, showPressed) {
      var group = groups[name];
      if (!group) return;
      if (!applicable) {
        group.hidden = true;
        group.classList.remove('is-collapsed');
        return;
      }
      var chipMode = (mode === 'collapsed' || mode === 'auto');
      group.hidden = (mode === 'unreached');
      group.classList.toggle('is-collapsed', chipMode);
      var chip = group.querySelector('[data-ppc-chip]');
      if (chip) {
        chip.hidden = !chipMode;
        chip.disabled = (mode === 'auto');
        if (chipMode) {
          var label = chip.querySelector('[data-ppc-chip-label]');
          if (label) label.textContent = chipLabelFor(group, value);
        }
      }
      Array.prototype.slice.call(group.querySelectorAll('[data-ppc-value]')).forEach(function (btn) {
        var v = btn.getAttribute('data-ppc-value');
        btn.hidden = !!allowed && allowed.indexOf(v) === -1;
        btn.setAttribute('aria-pressed', showPressed && v === value ? 'true' : 'false');
      });
    }

    function apply(broadcast, fromClick) {
      var stages = stagesFor(sel.state);
      if (stages.indexOf(sel.stage) === -1) { sel.stage = stages[0]; answered.stage = !guided; }
      var roles = rolesFor(sel.state, sel.stage);
      var roleActive = roles.length > 1;
      if (roleActive && roles.indexOf(sel.role) === -1) { sel.role = roles[0]; answered.role = !guided; }

      var applicable = {
        state: !!groups.state,
        stage: stages.length > 1,
        role: roleActive
      };

      var active = null;
      if (guided) {
        for (var i = 0; i < GROUP_ORDER.length; i++) {
          var n = GROUP_ORDER[i];
          if (applicable[n] && !answered[n]) { active = n; break; }
        }
      }

      var reached = true;
      GROUP_ORDER.forEach(function (name) {
        var allowed = name === 'stage' ? stages : name === 'role' ? roles : null;
        var group = groups[name];

        // Auto-applied level (only one valid option): once the path is
        // complete, surface it as a locked chip so the visitor sees what
        // was chosen for them. Skip when there's no matching choice card
        // (e.g. a shared both-roles course).
        if (guided && !applicable[name] && group) {
          var autoVal = allowed && allowed.length ? allowed[0] : sel[name];
          var hasBtn = !!group.querySelector('[data-ppc-value="' + autoVal + '"]');
          if (active === null && hasBtn) {
            renderGroup(name, true, 'auto', autoVal, null, true);
          } else {
            renderGroup(name, false, 'unreached', autoVal, null, false);
          }
          return;
        }

        var mode;
        if (!guided) {
          mode = 'expanded';
        } else if (name === active) {
          mode = 'expanded';
          reached = false;
        } else if (!reached) {
          mode = 'unreached';
        } else {
          mode = 'collapsed';
        }
        renderGroup(name, applicable[name], mode, sel[name], allowed, !guided || answered[name]);
      });

      // Path complete: offer a "See your <state> courses" CTA routing to that
      // path's course page (window.PCRoutes, from pc-routes.js) instead of the
      // on-page deck. Falls back to revealing the deck when no route exists or
      // inside the theme editor.
      var routeDest = null;
      if (guided && active === null && !stayHere && !(window.Shopify && window.Shopify.designMode)) {
        var routes = window.PCRoutes || {};
        var dest = routes[sel.state + '-' + sel.stage + '-' + sel.role];
        if (dest && dest !== window.location.pathname) { routeDest = dest; }
      }

      var gate = guided && (active !== null || routeDest !== null);
      var revealed = null;
      decks.forEach(function (d) {
        var on = !gate &&
          d.dataset.state === sel.state &&
          d.dataset.stage === sel.stage &&
          (!roleActive || d.dataset.role === sel.role || d.dataset.role === 'both');
        if (on && d.hidden) revealed = d;
        d.hidden = !on;
      });

      // Auto-route (team feedback 07-21): on the click that completes the path,
      // go straight to the course page — no confirm button. Only fromClick (a
      // deep-link that arrives complete still shows the CTA fallback), never in
      // the theme editor (routeDest is null there). 900ms beat with a "Taking
      // you to…" line so the read-back chips register before navigation; the
      // funnel page's read-back strip then carries the choices.
      if (autoRoute && guided && fromClick && active === null && routeDest && !navigating) {
        navigating = true;
        renderRouteCta(routeDest);
        if (routeCta) {
          var goingName = sel.state === 'CA' ? 'California' : sel.state === 'FL' ? 'Florida' : 'your';
          routeCta.textContent = 'Taking you to your ' + goingName + ' courses…';
          routeCta.setAttribute('aria-live', 'polite');
        }
        setTimeout(function () { window.location.assign(routeDest); }, 900);
      } else {
        renderRouteCta(routeDest);
      }

      // Path complete → let the read-back chips + CTA use the full row (they stay
      // inline, wrapping only when the viewport is too narrow) instead of the
      // narrow question column that would force them onto multiple lines.
      if (guideEl) guideEl.classList.toggle('is-complete', guided && active === null);

      if (guided && fromClick && active === null && !routeDest && revealed) {
        revealed.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (broadcast && !singleState) {
        document.dispatchEvent(new CustomEvent(SYNC_EVENT, {
          detail: { scope: 'state', value: sel.state, source: root }
        }));
      }
    }

    GROUP_ORDER.forEach(function (name) {
      var group = groups[name];
      if (!group) return;
      group.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-ppc-chip]');
        if (chip) {
          answered[name] = false;
          apply(false, false);
          return;
        }
        var btn = e.target.closest('[data-ppc-value]');
        if (!btn) return;
        sel[name] = btn.getAttribute('data-ppc-value');
        answered[name] = true;
        apply(name === 'state', true);
      });
    });

    document.addEventListener(SYNC_EVENT, function (e) {
      if (e.detail.source === root) return;
      if (e.detail.scope === 'state') {
        if (singleState) return;
        var known = decks.some(function (d) { return d.dataset.state === e.detail.value; });
        if (known) { sel.state = e.detail.value; answered.state = true; apply(false, false); }
      } else if (e.detail.scope === 'stage') {
        sel.stage = String(e.detail.value).toLowerCase();
        answered.stage = true;
        apply(false, false);
      } else if (e.detail.scope === 'role') {
        sel.role = String(e.detail.value).toLowerCase();
        answered.role = true;
        apply(false, false);
      }
    });

    apply(false, false);
  }

  function boot(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(ctx.querySelectorAll('[data-pc-pricing]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
